"use client";

import { useState, useEffect, useMemo } from "react";
import Link from "next/link";

// --- Types ---

interface Assignment {
    assignment_id: string;
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
    profile_photo_url?: string;
    member_status?: string;
    role: string;
    entity_type: string;
    entity_id: number;
    entity_name: string;
    entity_group: string;
    assignment_status: string;
}

function isInactive(p: { member_status?: string }): boolean {
    return (p.member_status || "").toLowerCase() === "inactive";
}

// ── Hierarchy definition ──────────────────────────────────────────────────────
// Each leaf entry maps to entity names (lowercased substring match) or types.
// We rely primarily on entity_name (lowercase contains) to bucket committees.
// Chapters and communities get explicit sections at the bottom.

type HierarchyNode =
    | { kind: "section"; label: string; children: HierarchyNode[] }
    | { kind: "leaf"; label: string; match: (a: Assignment) => boolean };

/**
 * Returns true when entity_name contains all of the given keywords (case-insensitive).
 */
function nameContains(a: Assignment, ...keywords: string[]): boolean {
    const n = a.entity_name.toLowerCase();
    return keywords.every((k) => n.includes(k.toLowerCase()));
}

const HIERARCHY: HierarchyNode[] = [
    {
        kind: "section",
        label: "Board of Directors",
        children: [
            {
                kind: "leaf",
                label: "Board of Directors",
                match: (a) => nameContains(a, "board of directors"),
            },
        ],
    },
    {
        kind: "section",
        label: "Committees of the Board",
        children: [
            { kind: "leaf", label: "Audit Committee", match: (a) => nameContains(a, "audit") },
            { kind: "leaf", label: "Executive Committee", match: (a) => nameContains(a, "executive committee") },
            { kind: "leaf", label: "Finance Committee", match: (a) => nameContains(a, "finance") },
            { kind: "leaf", label: "Governance Committee", match: (a) => nameContains(a, "governance") },
        ],
    },
    {
        kind: "section",
        label: "Committees of the Association",
        children: [
            {
                kind: "section",
                label: "The CMT Experience",
                children: [
                    { kind: "leaf", label: "Admissions Committee", match: (a) => nameContains(a, "admission") },
                    { kind: "leaf", label: "Curriculum & Testing Committee", match: (a) => nameContains(a, "curriculum") || nameContains(a, "testing") },
                    { kind: "leaf", label: "Ethics & Standards Committee", match: (a) => nameContains(a, "ethics") },
                ],
            },
            {
                kind: "section",
                label: "Thought Leadership",
                children: [
                    { kind: "leaf", label: "Charles H. Dow Award Committee", match: (a) => nameContains(a, "dow award") || nameContains(a, "charles") },
                    { kind: "leaf", label: "Journal of Technical Analysis Committee", match: (a) => nameContains(a, "journal") },
                ],
            },
            {
                kind: "section",
                label: "Outreach and Advocacy",
                children: [
                    { kind: "leaf", label: "Academic Outreach", match: (a) => nameContains(a, "academ") },
                    { kind: "leaf", label: "Industry Outreach", match: (a) => nameContains(a, "industry outreach") },
                ],
            },
            {
                kind: "section",
                label: "The Member Experience",
                children: [
                    { kind: "leaf", label: "Events (Summit/Symposium) Committee", match: (a) => nameContains(a, "event") || nameContains(a, "summit") || nameContains(a, "symposium") },
                    { kind: "leaf", label: "Technology & Data Intelligence Committee", match: (a) => nameContains(a, "technology") && nameContains(a, "intelligence") },
                    {
                        kind: "section",
                        label: "Global Chapter Development Committee",
                        children: [
                            { kind: "leaf", label: "Global Chapter Development Committee", match: (a) => (nameContains(a, "chapter development") || nameContains(a, "cdc")) && !nameContains(a, "amer") && !nameContains(a, "apac") && !nameContains(a, "emea") && !nameContains(a, "latam") && !nameContains(a, "north america") && !nameContains(a, "asia-pacific") && !nameContains(a, "europe") && !nameContains(a, "latin america") },
                            {
                                kind: "section",
                                label: "Thematic Subcommittees",
                                children: [
                                    { kind: "leaf", label: "Dual Charter Initiative", match: (a) => nameContains(a, "dual charter") },
                                    { kind: "leaf", label: "Global Black Caucus", match: (a) => nameContains(a, "black caucus") },
                                    { kind: "leaf", label: "Global Digital Network", match: (a) => nameContains(a, "digital network") },
                                    { kind: "leaf", label: "Women in Technical Analysis", match: (a) => nameContains(a, "women") },
                                ],
                            },
                        ],
                    },
                    { kind: "leaf", label: "Global Black Caucus", match: (a) => nameContains(a, "black caucus") && a.entity_type !== "committee" },
                    { kind: "leaf", label: "Global Digital Network", match: (a) => nameContains(a, "digital network") && a.entity_type !== "committee" },
                    { kind: "leaf", label: "Women in Technical Analysis", match: (a) => nameContains(a, "women") && a.entity_type !== "committee" },
                ],
            },
            {
                kind: "section",
                label: "Geographic Subcommittees",
                children: [
                    { kind: "leaf", label: "AMER Chapter Development Committee", match: (a) => nameContains(a, "amer") && nameContains(a, "chapter") },
                    { kind: "leaf", label: "APAC Chapter Development Committee", match: (a) => (nameContains(a, "apac") || nameContains(a, "asia-pacific")) && nameContains(a, "chapter") },
                    { kind: "leaf", label: "EMEA Chapter Development Committee", match: (a) => (nameContains(a, "emea") || nameContains(a, "europe")) && nameContains(a, "chapter") },
                    { kind: "leaf", label: "LATAM Chapter Development Committee", match: (a) => (nameContains(a, "latam") || nameContains(a, "latin america")) && nameContains(a, "chapter") },
                ],
            },
        ],
    },
    {
        kind: "section",
        label: "Chapters",
        children: [
            {
                kind: "leaf",
                label: "__all_chapters__",
                match: (a) => a.entity_type === "chapter",
            },
        ],
    },
    {
        kind: "section",
        label: "Communities",
        children: [
            {
                kind: "leaf",
                label: "__all_communities__",
                match: (a) => a.entity_type === "community",
            },
        ],
    },
];

// ── URL helper ────────────────────────────────────────────────────────────────
function groupUrl(entityType: string, entityId: number): string {
    const plural =
        entityType === "community"
            ? "communities"
            : entityType === "committee"
            ? "committees"
            : "chapters";
    return `/groups/${plural}/${entityId}`;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const API_BASE =
    typeof window !== "undefined"
        ? ""
        : process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001";

// ── Row building ──────────────────────────────────────────────────────────────
type TableRow =
    | { type: "section-header"; label: string; depth: number }
    | { type: "group-row"; groupName: string; list: Assignment[]; entityType: string; entityId: number };

/**
 * Walk the hierarchy tree and produce a flat list of TableRows.
 * A leaf "__all_chapters__" or "__all_communities__" expands into individual group rows.
 */
function buildRows(
    node: HierarchyNode,
    groupedByName: Map<string /* "entityType-entityId" */, Assignment[]>,
    depth: number,
    rows: TableRow[],
    matchedKeys: Set<string>
): void {
    if (node.kind === "section") {
        // Check if any descendant has data before emitting header
        const childRows: TableRow[] = [];
        node.children.forEach((child) =>
            buildRows(child, groupedByName, depth + 1, childRows, matchedKeys)
        );
        if (childRows.length > 0) {
            rows.push({ type: "section-header", label: node.label, depth });
            rows.push(...childRows);
        }
    } else {
        // leaf
        if (node.label === "__all_chapters__" || node.label === "__all_communities__") {
            const entityType = node.label === "__all_chapters__" ? "chapter" : "community";
            const matching: [string, Assignment[]][] = [];
            groupedByName.forEach((list, key) => {
                if (list[0].entity_type === entityType && !matchedKeys.has(key)) {
                    matching.push([key, list]);
                }
            });
            matching.sort((a, b) => a[1][0].entity_name.localeCompare(b[1][0].entity_name));
            matching.forEach(([key, list]) => {
                matchedKeys.add(key);
                rows.push({
                    type: "group-row",
                    groupName: list[0].entity_name,
                    list,
                    entityType: list[0].entity_type,
                    entityId: list[0].entity_id,
                });
            });
        } else {
            const matching: [string, Assignment[]][] = [];
            groupedByName.forEach((list, key) => {
                if (!matchedKeys.has(key) && node.match(list[0])) {
                    matching.push([key, list]);
                }
            });
            matching.sort((a, b) => a[1][0].entity_name.localeCompare(b[1][0].entity_name));
            matching.forEach(([key, list]) => {
                matchedKeys.add(key);
                rows.push({
                    type: "group-row",
                    groupName: list[0].entity_name,
                    list,
                    entityType: list[0].entity_type,
                    entityId: list[0].entity_id,
                });
            });
        }
    }
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function VolunteersPage() {
    return <VolunteersDirectory />;
}

export function VolunteersDirectory({ embedded = false }: { embedded?: boolean }) {
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        setLoading(true);
        fetch(`${API_BASE}/api/v1/organizations/assignments?limit=2000`)
            .then((res) => res.json())
            .then((data) => {
                setAssignments(data || []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    // Filter out ghost assignments with no name
    const validAssignments = useMemo(
        () => assignments.filter((a) => a.first_name?.trim() || a.last_name?.trim()),
        [assignments]
    );

    const filteredAssignments = useMemo(() => {
        const query = searchQuery.toLowerCase();
        return validAssignments.filter((a) => {
            const fullName = `${a.first_name} ${a.last_name}`.toLowerCase();
            return (
                fullName.includes(query) ||
                a.role.toLowerCase().includes(query) ||
                a.entity_name.toLowerCase().includes(query) ||
                a.entity_type.toLowerCase().includes(query)
            );
        });
    }, [validAssignments, searchQuery]);

    // Group by "entityType-entityId"
    const groupedByKey = useMemo(() => {
        const map = new Map<string, Assignment[]>();
        filteredAssignments.forEach((a) => {
            const key = `${a.entity_type}-${a.entity_id}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(a);
        });
        return map;
    }, [filteredAssignments]);

    // Build hierarchy rows
    const tableRows = useMemo(() => {
        const rows: TableRow[] = [];
        const matched = new Set<string>();
        HIERARCHY.forEach((node) => buildRows(node, groupedByKey, 0, rows, matched));
        // Append any unmatched entries at the end (catch-all)
        const unmatched: [string, Assignment[]][] = [];
        groupedByKey.forEach((list, key) => {
            if (!matched.has(key)) unmatched.push([key, list]);
        });
        unmatched.sort((a, b) => a[1][0].entity_name.localeCompare(b[1][0].entity_name));
        unmatched.forEach(([, list]) =>
            rows.push({
                type: "group-row",
                groupName: list[0].entity_name,
                list,
                entityType: list[0].entity_type,
                entityId: list[0].entity_id,
            })
        );
        return rows;
    }, [groupedByKey]);

    // Stats
    const stats = useMemo(() => {
        const uniqueVolunteers = new Set(validAssignments.map((a) => a.user_id));
        const uniqueGroups = new Set(validAssignments.map((a) => `${a.entity_type}-${a.entity_id}`));
        return {
            totalAssignments: validAssignments.length,
            volunteers: uniqueVolunteers.size,
            groups: uniqueGroups.size,
        };
    }, [validAssignments]);

    const PersonCell = ({ people }: { people: Assignment[] }) => {
        const valid = people.filter((p) => p.first_name?.trim() || p.last_name?.trim());
        if (valid.length === 0) return <span className="text-[#c0c0c0]">—</span>;
        return (
            <div className="flex flex-col gap-1.5">
                {valid.map((p) => {
                    const inactive = isInactive(p);
                    return (
                    <Link key={p.assignment_id} href={`/volunteers/${p.user_id}`} className="flex items-center gap-1.5 hover:opacity-75 transition-opacity cursor-pointer">
                        {p.profile_photo_url ? (
                            <img
                                src={p.profile_photo_url}
                                alt={`${p.first_name} ${p.last_name}`}
                                title={inactive ? "Inactive member" : undefined}
                                className={`w-6 h-6 rounded-full object-cover flex-shrink-0 ${inactive ? "grayscale opacity-50" : ""}`}
                            />
                        ) : (
                            <div
                                title={inactive ? "Inactive member" : undefined}
                                className={`w-6 h-6 rounded-full text-white text-[9px] font-black flex items-center justify-center uppercase flex-shrink-0 ${inactive ? "bg-[#b0b0b0] opacity-70" : "bg-[#003d45]"}`}
                            >
                                {(p.first_name?.[0] || "")}{(p.last_name?.[0] || "")}
                            </div>
                        )}
                        <span className={`font-medium text-xs leading-tight ${inactive ? "text-[#999]" : "text-[#1a1a1a]"}`}>
                            {p.first_name} {p.last_name}
                        </span>
                    </Link>
                    );
                })}
            </div>
        );
    };

    const MembersCell = ({ people }: { people: Assignment[] }) => {
        const valid = people.filter((p) => p.first_name?.trim() || p.last_name?.trim());
        if (valid.length === 0) return <span className="text-[#c0c0c0]">—</span>;
        return (
            <div className="flex flex-wrap gap-1.5 items-start content-start">
                {valid.map((a) => {
                    const inactive = isInactive(a);
                    return (
                    <Link key={a.assignment_id} href={`/volunteers/${a.user_id}`} className={`flex items-center gap-1.5 rounded-full px-2 py-1 transition-colors cursor-pointer ${inactive ? "bg-[#f0f0f0]/60 hover:bg-[#e6e6e6]" : "bg-[#f0f0f0] hover:bg-[#e0eaec]"}`}>
                        {a.profile_photo_url ? (
                            <img
                                src={a.profile_photo_url}
                                alt={`${a.first_name} ${a.last_name}`}
                                title={inactive ? "Inactive member" : undefined}
                                className={`w-6 h-6 rounded-full object-cover flex-shrink-0 ${inactive ? "grayscale opacity-50" : ""}`}
                            />
                        ) : (
                            <div
                                title={inactive ? "Inactive member" : undefined}
                                className={`w-6 h-6 rounded-full text-white text-[9px] font-black flex items-center justify-center uppercase flex-shrink-0 ${inactive ? "bg-[#b0b0b0] opacity-70" : "bg-[#007a8a]"}`}
                            >
                                {(a.first_name?.[0] || "")}{(a.last_name?.[0] || "")}
                            </div>
                        )}
                        <span className={`text-xs font-medium ${inactive ? "text-[#999]" : "text-[#1a1a1a]"}`}>
                            {a.first_name} {a.last_name}
                        </span>
                    </Link>
                    );
                })}
            </div>
        );
    };

    const DEPTH_INDENT = ["", "pl-4", "pl-8", "pl-12"];
    const DEPTH_BG = ["bg-[#e8f0ef]", "bg-[#f0f5f4]", "bg-[#f7faf9]", "bg-[#fbfcfc]"];
    const DEPTH_TEXT_SIZE = ["text-[10px]", "text-[9.5px]", "text-[9px]", "text-[9px]"];

    let dataRowIdx = 0;

    return (
        <main className={`bg-[#fbfcfa] font-sans selection:bg-[#003d45] selection:text-white ${embedded ? "rounded-[2rem] overflow-hidden pb-16" : "min-h-screen pb-32"}`}>
            {/* Hero Header */}
            <div className={`bg-[#003d45] text-white px-6 relative overflow-hidden ${embedded ? "pt-12 pb-24" : "pt-32 pb-24"}`}>
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-[#007a8a]/20 to-transparent rounded-full blur-3xl -mr-64 -mt-64" />
                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-12">
                        <div className="space-y-6 max-w-3xl">
                            <div className="inline-block px-3 py-1 bg-[#007a8a]/20 border border-[#007a8a]/30 rounded-md">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#007a8a]">
                                    Organizational Intelligence
                                </p>
                            </div>
                            <h1 className="text-6xl font-black tracking-tighter leading-none uppercase">
                                Global <span className="text-[#007a8a]">Assignments</span>
                            </h1>
                            <p className="text-white/60 text-lg font-medium leading-relaxed max-w-xl">
                                Real-time directory of volunteer roles across the CMT Association ecosystem,
                                from committees of the board to regional chapters and thematic communities.
                            </p>
                        </div>

                        {/* Stats Panel */}
                        <div className="grid grid-cols-3 gap-4 sm:gap-12 bg-white/5 backdrop-blur-xl border border-white/10 p-5 sm:p-8 rounded-2xl lg:mb-2">
                            <div>
                                <p className="text-[9px] font-black uppercase tracking-widest text-[#007a8a] mb-2">Total Roles</p>
                                <p className="text-3xl font-black text-white">{stats.totalAssignments}</p>
                            </div>
                            <div className="border-l border-white/10 pl-12">
                                <p className="text-[9px] font-black uppercase tracking-widest text-[#007a8a] mb-2">Volunteers</p>
                                <p className="text-3xl font-black text-white">{stats.volunteers}</p>
                            </div>
                            <div className="border-l border-white/10 pl-12">
                                <p className="text-[9px] font-black uppercase tracking-widest text-[#007a8a] mb-2">Units</p>
                                <p className="text-3xl font-black text-white">{stats.groups}</p>
                            </div>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div className="mt-16 max-w-3xl translate-y-32">
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-[#007a8a] to-[#003d45] rounded-2xl blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200" />
                            <div className="relative bg-white rounded-xl shadow-2xl p-2 flex items-center gap-2">
                                <div className="pl-4 text-[#666666]">
                                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="3">
                                        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Filter by volunteer name, role, or unit..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="flex-1 border-none py-4 px-2 text-lg text-[#1a1a1a] focus:ring-0 placeholder:text-[#999999]"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-[#666666] hover:text-[#ae232d] transition-colors"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="max-w-7xl mx-auto px-6 pt-56">
                {loading ? (
                    <div className="flex items-center justify-center py-40">
                        <div className="flex flex-col items-center gap-6">
                            <div className="w-12 h-12 border-4 border-[#e2e8f0] border-t-[#003d45] animate-spin rounded-full" />
                            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#666666]">
                                Synchronizing Registry
                            </p>
                        </div>
                    </div>
                ) : tableRows.length === 0 ? (
                    <div className="bg-white border border-[#f0f0f0] p-32 text-center rounded-3xl shadow-xl space-y-6">
                        <div className="w-16 h-16 bg-[#fbfcfa] rounded-full flex items-center justify-center mx-auto border border-[#f0f0f0]">
                            <svg width="24" height="24" fill="none" stroke="#666666" strokeWidth="2">
                                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <div className="space-y-2">
                            <h4 className="text-xl font-black text-[#003d45] uppercase">No Matching Assignments</h4>
                            <p className="text-[#666666] text-sm">
                                We couldn&apos;t find any assignments matching &ldquo;{searchQuery}&rdquo;
                            </p>
                        </div>
                        <button
                            onClick={() => setSearchQuery("")}
                            className="bg-[#f0f0f0] px-8 py-3 text-[10px] font-black uppercase tracking-widest text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white transition-all rounded-lg"
                        >
                            Reset Filter
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-2xl border border-[#e2e8f0] shadow-sm bg-white [-webkit-overflow-scrolling:touch]">
                        <table className="w-full min-w-[820px] text-sm border-collapse">
                            <thead>
                                <tr className="bg-[#003d45] text-white">
                                    <th className="text-left px-5 py-4 text-[10px] font-black uppercase tracking-[0.15em] w-[22%]">Group</th>
                                    <th className="text-left px-4 py-4 text-[10px] font-black uppercase tracking-[0.15em] w-[12%]">Chair</th>
                                    <th className="text-left px-4 py-4 text-[10px] font-black uppercase tracking-[0.15em] w-[12%]">Vice Chair</th>
                                    <th className="text-left px-4 py-4 text-[10px] font-black uppercase tracking-[0.15em] w-[26%]">Volunteers</th>
                                    <th className="text-left px-4 py-4 text-[10px] font-black uppercase tracking-[0.15em] w-[14%]">Board Liaison</th>
                                    <th className="text-left px-4 py-4 text-[10px] font-black uppercase tracking-[0.15em] w-[14%]">Staff Liaison</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableRows.map((item, idx) => {
                                    if (item.type === "section-header") {
                                        const depth = Math.min(item.depth, 3);
                                        const indent = DEPTH_INDENT[depth] || "";
                                        const bg = DEPTH_BG[depth] || DEPTH_BG[0];
                                        const textSize = DEPTH_TEXT_SIZE[depth] || "text-[9px]";
                                        const borderLeft =
                                            depth === 0
                                                ? "border-l-4 border-l-[#003d45]"
                                                : depth === 1
                                                ? "border-l-4 border-l-[#007a8a]"
                                                : "border-l-4 border-l-[#007a8a]/40";
                                        return (
                                            <tr key={`hdr-${idx}`}>
                                                <td
                                                    colSpan={6}
                                                    className={`px-5 py-2 border-b border-[#e2e8f0] ${bg} ${borderLeft}`}
                                                >
                                                    <span className={`${indent} ${textSize} font-black uppercase tracking-[0.2em] text-[#003d45]`}>
                                                        {item.label}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    }

                                    const { groupName, list, entityType, entityId } = item;
                                    const chairs = list.filter((a) => a.role === "Chair");
                                    const viceChairs = list.filter((a) => a.role === "Vice Chair");
                                    const staffLiaisons = list.filter((a) => a.role === "Staff Liaison");
                                    const boardLiaisons = list.filter((a) => a.role === "Board Liaison");
                                    const members = list.filter(
                                        (a) =>
                                            !["Chair", "Vice Chair", "Staff Liaison", "Board Liaison"].includes(a.role)
                                    );

                                    const rowBg = dataRowIdx++ % 2 === 0 ? "bg-white" : "bg-[#f7f9f8]";

                                    return (
                                        <tr
                                            key={`row-${entityType}-${entityId}`}
                                            className={`${rowBg} border-b border-[#e8eceb] hover:bg-[#e8f4f5] transition-colors group`}
                                        >
                                            <td className="px-5 py-3.5 border-l-2 border-l-transparent group-hover:border-l-[#007a8a] transition-colors align-top">
                                                <Link href={groupUrl(entityType, entityId)} className="block">
                                                    <p className="font-bold text-[#003d45] text-sm leading-snug group-hover:text-[#007a8a] transition-colors">
                                                        {groupName}
                                                    </p>
                                                    <span className="text-[9px] font-black uppercase tracking-[0.15em] text-[#007a8a] mt-0.5 block">
                                                        {entityType}
                                                    </span>
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3.5 align-top"><PersonCell people={chairs} /></td>
                                            <td className="px-4 py-3.5 align-top"><PersonCell people={viceChairs} /></td>
                                            <td className="px-4 py-3.5 align-top"><MembersCell people={members} /></td>
                                            <td className="px-4 py-3.5 align-top"><PersonCell people={boardLiaisons} /></td>
                                            <td className="px-4 py-3.5 align-top"><PersonCell people={staffLiaisons} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <style jsx global>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
                body {
                    font-family: 'Outfit', system-ui, sans-serif;
                    background-color: #fbfcfa;
                }
            `}</style>
        </main>
    );
}
